import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import {
  useBatchPreview,
  useDrawdownBatches,
  useBatchesPDFStatus,
  batchHasPdfActivity,
  hasPdfActivity,
} from '@/hooks/react-query/useDrawdownBatches';
import { queryKeys } from '@/hooks/react-query/queryKeys';
import { useDrawdown } from '@/hooks/react-query/useDrawdowns';
import { DrawdownBatchService } from '@/services/drawdowns/drawdownBatchService';
import type {
  BatchInvestorNotice,
  DrawdownGroup,
  DrawdownPayment,
} from '@/types/drawdown';
import { InvestorsTabel } from './InvestorsTable';
import { DrawdownSummary } from './DrawdownSummary';
import { DrawdownPreviewHeader } from './DrawdownPreviewHeader';
import { DrawdownBatchNavigation } from './DrawdownBatchNavigation';
import { DrawdownBatchDetails } from './DrawdownBatchDetails';
import { DrawdownBatchActions } from './DrawdownBatchActions';
import { DrawdownPreviewLoadingState } from './DrawdownPreviewLoadingState';
import { DrawdownPreviewEmptyState } from './DrawdownPreviewEmptyState';
import { RecordPaymentModal } from '../RecordPaymentModal';
import { InvestorPaymentsDialog } from './InvestorPaymentsDialog';
import { EditNoticeAmountDialog } from './EditNoticeAmountDialog';

interface DrawdownPreviewViewProps {
  fundId: string;
  drawdownId: string;
  initialBatchId?: string;
  onStatusUpdate?: (drawdownId: string, status: string) => Promise<void>;
  isUpdatingStatus?: boolean;
}

export function DrawdownPreviewView({
  fundId,
  drawdownId,
  initialBatchId,
  onStatusUpdate,
  isUpdatingStatus = false,
}: DrawdownPreviewViewProps) {
  const fundUrl = `/app/funds/${fundId}?tab=overview`;
  const drawdownsUrl = `/app/funds/${fundId}?tab=transactions&section=drawdowns`;

  const { data: drawdown, isLoading: drawdownLoading } = useDrawdown(
    drawdownId,
    { fundId, includeInvestors: true, includePayments: true }
  );
  const { data: batches = [], isLoading: batchesLoading } = useDrawdownBatches(
    fundId,
    drawdownId
  );

  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [recordPaymentInvestor, setRecordPaymentInvestor] =
    useState<BatchInvestorNotice | null>(null);
  const [viewPaymentsInvestor, setViewPaymentsInvestor] =
    useState<BatchInvestorNotice | null>(null);
  const [editAmountInvestor, setEditAmountInvestor] =
    useState<BatchInvestorNotice | null>(null);

  const currentBatchId =
    selectedBatchId || initialBatchId || batches[0]?.id || null;
  const currentBatchIndex = batches.findIndex(
    (batch) => batch.id === currentBatchId
  );
  const currentBatch =
    currentBatchIndex >= 0 ? batches[currentBatchIndex] : null;

  const { data: batchPreview, isLoading: previewLoading } = useBatchPreview(
    fundId,
    drawdownId,
    currentBatchId || ''
  );

  const investorNotices = batchPreview?.investor_notices || [];

  // Payments hang off the drawdown investor, not the batch notice, so the table
  // and the dialog read them from the drawdown the page already fetched.
  const paymentsByInvestorId = useMemo(() => {
    const byInvestor: Record<string, DrawdownPayment[]> = {};
    for (const investor of drawdown?.drawdown?.drawdown_investors ?? []) {
      byInvestor[investor.investor_id] = investor.payments ?? [];
    }
    return byInvestor;
  }, [drawdown]);

  // Poll while the server-side queue is generating documents for any batch
  const { data: pdfStatusBatches } = useBatchesPDFStatus(fundId, drawdownId, {
    refetchInterval: (query) =>
      hasPdfActivity(query.state.data) ? 3000 : false,
  });
  const currentBatchPdfStatus = pdfStatusBatches?.find(
    (b) => b.id === currentBatchId
  );
  const docsGenerating =
    !!currentBatchPdfStatus && batchHasPdfActivity(currentBatchPdfStatus);

  // Refresh investor notices as documents complete so per-investor download
  // buttons light up progressively
  const queryClient = useQueryClient();
  const pdfsCompleted = currentBatchPdfStatus?.pdfs_completed;
  useEffect(() => {
    if (currentBatchId && pdfsCompleted) {
      queryClient.invalidateQueries({
        queryKey: [
          ...queryKeys.drawdowns.detail(drawdownId),
          'batches',
          currentBatchId,
          'preview',
        ],
      });
    }
  }, [pdfsCompleted, currentBatchId, drawdownId, queryClient]);

  const handleRecordPayment = (investor: BatchInvestorNotice) => {
    setRecordPaymentInvestor(investor);
  };

  const handleViewPayments = (investor: BatchInvestorNotice) => {
    setViewPaymentsInvestor(investor);
  };

  // LPs may pay before the call is issued; a cancelled call takes nothing.
  const canRecordPayment =
    !!drawdown?.drawdown &&
    ['draft', 'issued', 'partial', 'completed'].includes(
      drawdown.drawdown.status
    );
  const canEditAmounts = drawdown?.drawdown?.status === 'draft';

  // Approving the call writes its ledger, and a written ledger is not unwound
  // from here - the server refuses the unlink, so the page stops offering it.
  const canUnlinkPayments = drawdown?.can_change_bank_confirmation ?? false;

  // Emails can only be sent once the drawdown has left draft.
  const canSendEmails =
    !!drawdown?.drawdown && drawdown.drawdown.status.toLowerCase() !== 'draft';

  // Shape the drawdown for the shared Send-Emails dialog (mirrors the list view).
  const dd = drawdown?.drawdown;
  const sendEmailsGroup: DrawdownGroup | null = dd
    ? {
        drawdownId: dd.id,
        drawdownNumber: String(dd.drawdown_number),
        fundName: dd.fund?.name ?? '',
        purpose: dd.purpose,
        description: dd.description,
        totalAmount: dd.total_amount,
        status: dd.status,
        issueDate: dd.issue_date,
        batches: batches.map((batch) => ({
          batchId: batch.id,
          batchNumber: batch.batch_number,
          noticeDate: batch.actual_notice_date ?? batch.notice_date,
          dueDate: batch.due_date,
          investors: batch.investor_count,
          amount: batch.total_amount,
          status: batch.status,
          invoicesSent: batch.emails_sent,
          invoicesTotal: batch.emails_total,
          pdfGenerated: batch.pdfs_completed > 0,
          pdfs_completed: batch.pdfs_completed,
          pdfs_failed: batch.pdfs_failed,
          pdfs_total: batch.pdfs_total,
        })),
        createdAt: dd.created_at,
        lastUpdated: dd.updated_at,
        totalInvestorCount: dd.investors_total_count,
        totalBatchCount: batches.length,
      }
    : null;

  if (drawdownLoading || batchesLoading) {
    return <DrawdownPreviewLoadingState />;
  }

  return (
    <div className="min-h-full p-6 flex flex-col gap-3">
      <div className="space-y-4 p-6">
        <DrawdownPreviewHeader
          drawdown={drawdown?.drawdown}
          issueValidation={drawdown?.issue_validation ?? null}
          fundId={fundId}
          drawdownId={drawdownId}
          onStatusUpdate={onStatusUpdate}
          isUpdatingStatus={isUpdatingStatus}
          docsGenerating={docsGenerating}
          sendEmailsGroup={sendEmailsGroup}
          canSendEmails={canSendEmails}
          backTo={drawdownsUrl}
          breadcrumbs={[
            { label: 'Fund', href: fundUrl },
            { label: 'Transactions', href: drawdownsUrl },
            { label: 'Drawdown' },
          ]}
        />

        {batches.length === 0 ? (
          <DrawdownPreviewEmptyState />
        ) : (
          <>
            {drawdown?.drawdown && (
              <DrawdownSummary
                totalAmount={drawdown.drawdown.total_amount}
                totalAmountReceived={drawdown.drawdown.total_amount_received}
                investorsPaidCount={drawdown.drawdown.investors_paid_count}
                investorsTotalCount={drawdown.drawdown.investors_total_count}
                currency={drawdown.drawdown.currency}
              />
            )}

            {currentBatch && currentBatchId && (
              <>
                <DrawdownBatchNavigation
                  batches={batches}
                  currentBatchId={currentBatchId}
                  currentBatchIndex={currentBatchIndex}
                  onBatchChange={setSelectedBatchId}
                />

                <DrawdownBatchDetails
                  currentBatch={currentBatch}
                  actions={
                    <DrawdownBatchActions
                      investorNotices={investorNotices}
                      fundId={fundId}
                      drawdownId={drawdownId}
                      batchId={currentBatchId}
                      docsGenerating={docsGenerating}
                    />
                  }
                />
              </>
            )}

            {docsGenerating && currentBatchPdfStatus && (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/50 px-4 py-2.5 text-sm text-muted-foreground">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>
                  Generating documents… {currentBatchPdfStatus.pdfs_completed}/
                  {currentBatchPdfStatus.pdfs_total}
                </span>
              </div>
            )}

            <div
              className={previewLoading ? 'opacity-50 pointer-events-none' : ''}
            >
              <InvestorsTabel
                investorNotices={investorNotices}
                paymentsByInvestorId={paymentsByInvestorId}
                onDownloadPdf={(notificationId) =>
                  DrawdownBatchService.downloadInvestorPdfByNotification(
                    fundId,
                    drawdownId,
                    currentBatchId || '',
                    notificationId
                  )
                }
                onPreviewNotification={(investorId) =>
                  DrawdownBatchService.previewInvestorNotification(
                    fundId,
                    drawdownId,
                    currentBatchId || '',
                    investorId
                  )
                }
                onViewPayments={handleViewPayments}
                onRecordPayment={
                  canRecordPayment ? handleRecordPayment : undefined
                }
                onEditAmount={
                  canEditAmounts ? setEditAmountInvestor : undefined
                }
              />
            </div>

            {editAmountInvestor && (
              <EditNoticeAmountDialog
                onClose={() => setEditAmountInvestor(null)}
                fundId={fundId}
                drawdownId={drawdownId}
                notice={editAmountInvestor}
              />
            )}

            {viewPaymentsInvestor && dd && (
              <InvestorPaymentsDialog
                open={!!viewPaymentsInvestor}
                onClose={() => setViewPaymentsInvestor(null)}
                fundId={fundId}
                drawdownId={drawdownId}
                investorName={viewPaymentsInvestor.investor_name}
                payments={
                  paymentsByInvestorId[viewPaymentsInvestor.investor_id] ?? []
                }
                currency={dd.currency}
                canUnlink={canUnlinkPayments}
              />
            )}

            {recordPaymentInvestor && drawdown?.drawdown && (
              <RecordPaymentModal
                open={!!recordPaymentInvestor}
                onClose={() => setRecordPaymentInvestor(null)}
                onSuccess={() => setRecordPaymentInvestor(null)}
                fundId={fundId}
                drawdownId={drawdownId}
                currency={drawdown.drawdown.currency}
                investor={recordPaymentInvestor}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
